/**
 * 消費一覧・消費詳細APIクライアント（FKV3-13046）
 * バックエンドの /credit/consumption を呼び出す。月は `YYYY-MM`、数値は10進の文字列、値のないものは null。
 * お気に入りの登録と解除は、契約一覧と同じ API（credit-contracts の addFavorite・removeFavorite）を使う。
 */

import { apiGet } from './client';
import type { PricingPlan } from './credit-contracts';

export interface CategoryColumns {
  /** 表示区分（`display_category_type` の値） */
  category: string;
  /** 消費項目（`consumption_item_type` の値）。列挙型の序数の順 */
  items: string[];
}

export interface ContractView {
  pricing_plan: PricingPlan | null;
  contract_credit: string | null;
  monthly_fee: string | null;
}

/** 消費項目ごとの値と、消費項目が2つ以上属する表示区分の合計、すべての消費項目の合計 */
export interface ItemValues {
  total: string;
  /** 行のない消費項目は "0" */
  items: Record<string, string>;
  category_totals: Record<string, string>;
}

export interface MonthConsumption {
  total: string;
  /** 消費項目ごとの消費。行のない消費項目は "0" */
  items: Record<string, string>;
  /** 消費項目が2つ以上属する表示区分の合計 */
  category_totals: Record<string, string>;
  /** 実行消費のUSD。実行消費の消費項目と、その表示区分の合計だけを持つ */
  usd: ItemValues;
}

export interface CompanySnapshots {
  store_sku: string | null;
  s3_tb: string | null;
  user_count: string | null;
  /** ストアエージェントの店舗数のうち店舗向け。契約していなければ null */
  store_agent_store_sellers: string | null;
  /** ストアエージェントの店舗数のうち本部向け。契約していなければ null */
  store_agent_hub_sellers: string | null;
}

export interface HistoryCell {
  month: string;
  consumption: string | null;
  /** 指定した月の契約クレジット数に対する割合（%） */
  ratio: string | null;
}

export interface CompanyRow {
  id: string;
  name: string;
  favorite: boolean;
  contract: ContractView;
  /** 企業の最初の月より前の月は null */
  consumption: MonthConsumption | null;
  ratio: string | null;
  forecast: string | null;
  forecast_ratio: string | null;
  /** 指定した月の前の6か月（新しい月から） */
  history: HistoryCell[];
  /** 過去6か月のうち、企業の最初の月以後の月の消費の平均。月の途中から始まった最初の月は除く */
  history_average: string | null;
  /** 過去6か月の平均の、指定した月の契約クレジット数に対する割合（%） */
  history_average_ratio: string | null;
  snapshots: CompanySnapshots;
  data_connect: string | null;
}

export interface ConsumptionList {
  /** 当月（日本時間）。ブラウザの時計でなくこれを使う */
  current_month: string;
  month: string;
  /** 指定できる最初の月。null なら当月だけを指定できる */
  first_month: string | null;
  /** ISO 8601（UTC） */
  reflected_at: string | null;
  categories: CategoryColumns[];
  /** 実行消費の消費項目だけの表示区分（USDの列を置く消費項目） */
  execution_categories: CategoryColumns[];
  /** 企業IDの順 */
  companies: CompanyRow[];
}

/** ユーザーごとの内訳の1行。メールアドレスが同じユーザーは、ユーザーIDの発行元によらず1行にまとまる */
export interface UserRow {
  /** 行を識別する値 */
  key: string;
  /** ユーザーに紐付かない消費の行 */
  no_user: boolean;
  name: string | null;
  /** メールアドレスが空文字列か空白だけのユーザーは null */
  email: string | null;
  deleted: boolean;
  items: Record<string, string>;
  category_totals: Record<string, string>;
  total: string;
  usd: ItemValues;
  /** 構成比（%）。全行の合計の和が0なら null */
  share: string | null;
}

export interface AccountRow {
  account_id: string;
  /** CommonDB にないアカウントは null */
  code: string | null;
  name: string | null;
  snapshots: {
    store_sku: string | null;
    s3_tb: string | null;
    store_agent_store_sellers: string | null;
    store_agent_hub_sellers: string | null;
  };
  items: Record<string, string>;
  category_totals: Record<string, string>;
  total: string;
  usd: ItemValues;
}

/**
 * 機能ごとの内訳の行の種類。`Unclassified` は「未分類」、`NoFeatureCode` は「機能コードなし」の行。
 * `Feature` は、機能区分の行（「未分類」の行を含む）の下の機能の行
 */
export type FeatureRowKind = 'Category' | 'Unclassified' | 'NoFeatureCode' | 'Feature';

export interface FeatureRow {
  kind: FeatureRowKind;
  account_id: string;
  /** CommonDB にないアカウントは null */
  account_code: string | null;
  account_name: string | null;
  /** 機能区分の行だけ */
  feature_category_code: string | null;
  feature_category_name: string | null;
  /** 機能の行だけ */
  feature_code: string | null;
  /** 機能の行だけ。機能のテーブルにない機能コードは null */
  feature_name: string | null;
  items: Record<string, string>;
  category_totals: Record<string, string>;
  total: string;
  usd: ItemValues;
  /** 構成比（%）。機能区分の行と「機能コードなし」の行の合計の和が0なら null */
  share: string | null;
  /** 機能区分の行の下の、機能の行（合計の大きい順）。ほかの行では空 */
  features: FeatureRow[];
}

export interface MonthDetail {
  month: string;
  contract: ContractView;
  snapshots: CompanySnapshots;
  data_connect: string | null;
  consumption: MonthConsumption | null;
  ratio: string | null;
}

export interface ConsumptionDetail {
  current_month: string;
  company: { id: string; name: string };
  reflected_at: string | null;
  categories: CategoryColumns[];
  /** 実行消費の消費項目だけの表示区分 */
  execution_categories: CategoryColumns[];
  current: {
    consumption: string | null;
    ratio: string | null;
    forecast: string | null;
    forecast_ratio: string | null;
  };
  /** 当月と、先月から遡って最大36か月（新しい月から） */
  months: MonthDetail[];
  /** ユーザーごと・アカウントごと・機能ごとの内訳の月（指定した月。なければ当月。範囲の外なら範囲の端の月） */
  selected_month: string;
  /** 選んだ月の、ユーザーごとの内訳（合計の大きい順） */
  users: UserRow[];
  /** 選んだ月の、アカウントごとの内訳（アカウントコードの順） */
  accounts: AccountRow[];
  /** 選んだ月の、機能ごとの内訳（アカウントコードの順。同じアカウントでは、機能区分の並び順、「未分類」、「機能コードなし」の順） */
  features: FeatureRow[];
}

const base = '/credit/consumption';

export function getConsumptionList(month: string | null, authHeaders?: Record<string, string>) {
  const query = month ? `?${new URLSearchParams({ month })}` : '';
  return apiGet<ConsumptionList>(`${base}${query}`, authHeaders);
}

/** 消費詳細。内訳は `month`（`YYYY-MM`。なければ当月）の分だけ返る */
export function getConsumptionDetail(
  companyId: string,
  month: string | null,
  authHeaders?: Record<string, string>,
) {
  const query = month ? `?${new URLSearchParams({ month })}` : '';
  return apiGet<ConsumptionDetail>(`${base}/${encodeURIComponent(companyId)}${query}`, authHeaders);
}
